<script setup lang="ts">
import { watch } from 'vue';
import type { Approval, Invoice, TaskClientResponseMessage, TaskClientResponseStatus } from '~/types/models';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import TaskClientResponseThread from '@/components/tasks/TaskClientResponseThread.vue';
import ApprovalCard from '@/components/projects/ApprovalCard.vue';
import ConversationMessageCard from '@/components/workspace/ConversationMessageCard.vue';
import InvoiceActionCard from '@/components/workspace/InvoiceActionCard.vue';
import ActionSummaryCard from '@/components/workspace/ActionSummaryCard.vue';
import OnboardingActionCard from '@/components/workspace/OnboardingActionCard.vue';

type ActionItemsDialogButton = {
    label: string;
    disabled?: boolean;
};

type ActionItemsDialogMetaChip = {
    label: string;
    tone?: 'default' | 'muted' | 'warning';
};

type ActionItemsDialogThreadPresentation = {
    promptMessage?: string;
    responseStatus?: TaskClientResponseStatus | string;
    thread?: TaskClientResponseMessage[];
    emptyState?: string;
    showPromptMessage?: boolean;
};

type ActionItemsDialogMessagePresentation = {
    title?: string;
    actorName?: string;
    channelLabel?: string;
    body: string;
    timestamp?: unknown;
};

type ActionItemsDialogInvoicePresentation = {
    invoice: Invoice;
    projectName?: string;
    currency?: string;
};

type ActionItemsDialogDetailField = {
    label: string;
    value: string;
};

type ActionItemsDialogOnboardingPresentation = {
    mode?: 'requirement' | 'handoff';
    summary: string;
    statusLabel?: string;
    stageLabel?: string;
    projectName?: string;
    requirementType?: string;
    templateName?: string;
    linkedProjectsLabel?: string;
};

type ActionItemsDialogItem = {
    id: string;
    title: string;
    presentation?: 'summary' | 'thread' | 'approval' | 'message' | 'invoice' | 'onboarding';
    subtitle?: string;
    summary?: string;
    detailRows?: string[];
    detailFields?: ActionItemsDialogDetailField[];
    metaChips?: ActionItemsDialogMetaChip[];
    badge?: string;
    thread?: ActionItemsDialogThreadPresentation;
    approval?: Approval;
    message?: ActionItemsDialogMessagePresentation;
    invoice?: ActionItemsDialogInvoicePresentation;
    onboarding?: ActionItemsDialogOnboardingPresentation;
    primaryAction?: ActionItemsDialogButton;
    secondaryAction?: ActionItemsDialogButton;
};

const props = withDefaults(defineProps<{
    open: boolean;
    title: string;
    description: string;
    items: ActionItemsDialogItem[];
    emptyTitle?: string;
    emptyDescription?: string;
    initialOpenItemId?: string | null;
    approvalCanRespond?: boolean;
    approvalCanRemind?: boolean;
}>(), {
    emptyTitle: 'No actions right now',
    emptyDescription: 'When work needs attention, it will surface here.',
    initialOpenItemId: null,
    approvalCanRespond: false,
    approvalCanRemind: false,
});

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'primary-action', itemId: string): void;
    (e: 'secondary-action', itemId: string): void;
    (e: 'approval-respond', itemId: string): void;
    (e: 'approval-remind', itemId: string): void;
}>();

const expandedItemId = ref('');

function resetExpandedItems() {
    const preferredId = props.initialOpenItemId && props.items.some((item) => item.id === props.initialOpenItemId)
        ? props.initialOpenItemId
        : null;
    expandedItemId.value = preferredId || '';
}

function metaChipClass(tone: ActionItemsDialogMetaChip['tone'] = 'default') {
    if (tone === 'warning') {
        return 'border-amber-400/70 bg-amber-500/10 text-amber-800 dark:border-amber-300/50 dark:bg-amber-500/10 dark:text-amber-100';
    }

    if (tone === 'muted') {
        return 'border-border/60 bg-background/70 text-muted-foreground';
    }

    return 'border-border/70 bg-background/80 text-foreground/80';
}

watch(
    () => ({
        open: props.open,
        initialOpenItemId: props.initialOpenItemId,
        itemIds: props.items.map((item) => item.id).join('|'),
    }),
    ({ open }) => {
        if (!open) {
            expandedItemId.value = '';
            return;
        }

        resetExpandedItems();
    },
    { immediate: true },
);
</script>

<template>
    <Sheet :open="open" @update:open="(value) => emit('update:open', value)">
        <SheetContent class="w-full overflow-y-auto sm:max-w-2xl">
            <SheetHeader class="text-left">
                <SheetTitle>{{ title }}</SheetTitle>
                <SheetDescription>{{ description }}</SheetDescription>
            </SheetHeader>

            <div v-if="items.length === 0" class="rounded-3xl border border-dashed border-border/70 bg-background/50 px-5 py-5">
                <p class="text-sm font-semibold text-foreground">{{ emptyTitle }}</p>
                <p class="mt-2 text-sm text-muted-foreground">{{ emptyDescription }}</p>
            </div>

            <Accordion
                v-else
                v-model="expandedItemId"
                type="single"
                collapsible
                class="space-y-3"
            >
                <AccordionItem
                    v-for="item in items"
                    :key="item.id"
                    :value="item.id"
                    class="overflow-hidden rounded-3xl border border-border/60 bg-background/72"
                >
                    <AccordionTrigger class="items-center px-5 py-4 hover:no-underline">
                        <p class="min-w-0 flex-1 text-sm font-semibold text-foreground">{{ item.title }}</p>
                    </AccordionTrigger>

                    <AccordionContent class="border-t border-border/50 px-5 pt-4 pb-4">
                        <div class="space-y-3">
                            <div v-if="item.badge || item.subtitle" class="flex flex-wrap items-center gap-2">
                                <Badge v-if="item.badge" variant="outline">{{ item.badge }}</Badge>
                                <p v-if="item.subtitle" class="text-xs text-muted-foreground">{{ item.subtitle }}</p>
                            </div>

                            <TaskClientResponseThread
                                v-if="item.presentation === 'thread' && item.thread"
                                :prompt-message="item.thread.promptMessage"
                                :response-status="item.thread.responseStatus"
                                :thread="item.thread.thread"
                                :empty-state="item.thread.emptyState"
                                :show-prompt-message="item.thread.showPromptMessage"
                            />

                            <ApprovalCard
                                v-else-if="item.presentation === 'approval' && item.approval"
                                :approval="item.approval"
                                :can-respond="approvalCanRespond"
                                :can-remind="approvalCanRemind"
                                @respond="emit('approval-respond', item.id)"
                                @remind="emit('approval-remind', item.id)"
                            />

                            <ConversationMessageCard
                                v-else-if="item.presentation === 'message' && item.message"
                                :title="item.message.title"
                                :actor-name="item.message.actorName"
                                :channel-label="item.message.channelLabel"
                                :body="item.message.body"
                                :timestamp="item.message.timestamp"
                            />

                            <InvoiceActionCard
                                v-else-if="item.presentation === 'invoice' && item.invoice"
                                :invoice="item.invoice.invoice"
                                :project-name="item.invoice.projectName"
                                :currency="item.invoice.currency"
                            />

                            <OnboardingActionCard
                                v-else-if="item.presentation === 'onboarding' && item.onboarding"
                                :mode="item.onboarding.mode"
                                :summary="item.onboarding.summary"
                                :status-label="item.onboarding.statusLabel"
                                :stage-label="item.onboarding.stageLabel"
                                :project-name="item.onboarding.projectName"
                                :requirement-type="item.onboarding.requirementType"
                                :template-name="item.onboarding.templateName"
                                :linked-projects-label="item.onboarding.linkedProjectsLabel"
                            />

                            <ActionSummaryCard
                                v-else-if="item.summary || item.detailRows?.length || item.detailFields?.length"
                                :summary="item.summary"
                                :detail-rows="item.detailRows"
                                :detail-fields="item.detailFields"
                            />

                            <div v-if="item.metaChips?.length" class="flex flex-wrap gap-2">
                                <span
                                    v-for="chip in item.metaChips"
                                    :key="chip.label"
                                    :class="[
                                        'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium',
                                        metaChipClass(chip.tone),
                                    ]"
                                >
                                    {{ chip.label }}
                                </span>
                            </div>

                            <div class="flex flex-wrap gap-2 pt-1">
                                <Button
                                    v-if="item.primaryAction && !(item.presentation === 'approval' && approvalCanRespond)"
                                    size="sm"
                                    class="rounded-full"
                                    :disabled="item.primaryAction.disabled"
                                    @click="emit('primary-action', item.id)"
                                >
                                    {{ item.primaryAction.label }}
                                </Button>
                                <Button
                                    v-if="item.secondaryAction"
                                    size="sm"
                                    variant="outline"
                                    class="rounded-full"
                                    :disabled="item.secondaryAction.disabled"
                                    @click="emit('secondary-action', item.id)"
                                >
                                    {{ item.secondaryAction.label }}
                                </Button>
                            </div>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </SheetContent>
    </Sheet>
</template>
